import { useState } from 'react';
import { Plus, RefreshCw, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { aiSuggestService } from '@/services/aiSuggestService';
import { FEATURE_FLAGS } from '@/config/environment';

const DISMISS_KEY = 'bb-ai-dismissed-v1';

const readDismissed = (sectionId: string): string[] => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw);
    return all[sectionId] || [];
  } catch {
    return [];
  }
};

const writeDismissed = (sectionId: string, list: string[]) => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[sectionId] = list;
    localStorage.setItem(DISMISS_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / privacy mode failures
  }
};

export interface AISuggestProps {
  /** Stable id for sticky-dismissal storage. */
  sectionId: string;
  /** Called with `{existing, dismissed}` so the prompt can avoid duplicates.
   *  Optional when a custom `fetcher` is supplied. */
  getPrompt?: (ctx: { existing: string[]; dismissed: string[] }) => string;
  /** Optional custom fetcher — overrides the default aiSuggestService.suggest so a
   *  caller can plug in its own (grounded) backend while reusing this exact UI. */
  fetcher?: (ctx: { existing: string[]; dismissed: string[] }) => Promise<{ items: string[]; source?: string }>;
  /** Already-chosen items — excluded from results. */
  existing?: string[];
  /** chips = add-one-at-a-time. text = rewrite cards. */
  kind?: 'chips' | 'text';
  onAccept?: (item: string) => void;
  onAcceptText?: (text: string) => void;
  /** Idle-state pill label, e.g. "Suggest services". */
  placeholder?: string;
  /** Provenance line shown alongside results, e.g. "from your homepage". */
  source?: string;
  /**
   * Whether this instance belongs to the Brand Bible AI-suggest rollout.
   *
   * The release flag gates that FEATURE, but this is a shared UI component.
   * Site Health's Quick Scan reuses the chips with its own grounded backend
   * and is not part of that rollout — on `main` it inherited the flag and so
   * rendered nothing in production while dev (which has no gate) worked.
   *
   * Defaults to true, so every Brand Bible caller keeps its release gate and
   * the behaviour pinned by AISuggest.releaseFlag.test.tsx is unchanged.
   */
  releaseGated?: boolean;
  maxShow?: number;
}

type State = 'idle' | 'loading' | 'loaded' | 'error';

export function AISuggest({
  sectionId,
  getPrompt,
  fetcher,
  existing = [],
  onAccept,
  onAcceptText,
  kind = 'chips',
  placeholder = 'Suggest',
  source = 'AI inferred',
  maxShow = 5,
  releaseGated = true,
}: AISuggestProps) {
  const [state, setState] = useState<State>('idle');
  const [items, setItems] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed(sectionId));
  // Provenance line shown beside results. Starts at the prop default and is replaced by
  // whatever the backend returns (e.g. "demo data (backend pending)" when the route 404s).
  // Stored in state because mutating the destructured `source` prop has no render effect.
  const [displaySource, setDisplaySource] = useState(source);

  if (releaseGated && !FEATURE_FLAGS.brandBibleAiSuggest) return null;

  const fetchSuggestions = async () => {
    setState('loading');
    try {
      let raw: string[];
      let returnedSource: string | undefined;
      if (fetcher) {
        const res = await fetcher({ existing, dismissed });
        raw = res.items;
        returnedSource = res.source;
      } else {
        const prompt = getPrompt ? getPrompt({ existing, dismissed }) : '';
        const res = await aiSuggestService.suggest({ prompt, kind, existing, dismissed }, sectionId);
        raw = res.items;
        returnedSource = res.source;
      }
      const lcExisting = new Set(existing.map((x) => x.toLowerCase()));
      const lcDismissed = new Set(dismissed.map((x) => x.toLowerCase()));
      const filtered =
        kind === 'chips'
          ? raw
              .filter(Boolean)
              .filter((p) => !lcExisting.has(p.toLowerCase()))
              .filter((p) => !lcDismissed.has(p.toLowerCase()))
              .slice(0, 10)
          : raw.slice(0, 3);
      setItems(filtered);
      setDisplaySource(returnedSource || source);
      setState('loaded');
    } catch (err) {
      console.error('aiSuggest failed', err);
      setState('error');
    }
  };

  const acceptItem = (item: string) => {
    if (kind === 'chips') {
      onAccept?.(item);
      setItems((prev) => prev.filter((x) => x !== item));
    } else {
      onAcceptText?.(item);
      setState('idle');
      setItems([]);
    }
  };

  const dismissItem = (item: string) => {
    // Functional update + localStorage write so rapid clicks don't drop earlier dismissals
    // by spreading a stale snapshot of `dismissed`.
    setDismissed((prev) => {
      const next = prev.includes(item) ? prev : [...prev, item];
      writeDismissed(sectionId, next);
      return next;
    });
    setItems((prev) => prev.filter((x) => x !== item));
  };

  const dismissAll = () => {
    setState('idle');
    setItems([]);
  };

  const visible = showAll ? items : items.slice(0, maxShow);
  const hiddenCount = Math.max(0, items.length - maxShow);

  if (state === 'idle') {
    return (
      <button
        type="button"
        onClick={fetchSuggestions}
        className="animate-ai-suggest inline-flex items-center gap-1.5 pl-2.5 pr-2.5 py-1.5 rounded-md bg-teal-light text-teal-dark border border-teal/40 text-[12px] font-semibold cursor-pointer hover:bg-teal-light/80"
      >
        <Sparkles className="size-3.5" />
        {placeholder}
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <div className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-md bg-teal-light text-teal-dark border border-teal/40 text-[12px] font-semibold">
        <Sparkles className="size-3.5 animate-spin" />
        Thinking…
      </div>
    );
  }

  if (state === 'error') {
    return (
      <button
        type="button"
        onClick={fetchSuggestions}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))] border border-[hsl(var(--status-error)/0.3)] text-[12px] font-semibold cursor-pointer"
      >
        Couldn't fetch · retry
      </button>
    );
  }

  if (kind === 'chips') {
    if (items.length === 0) {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-transparent text-muted-foreground border border-dashed border-border text-[11.5px]">
          <span>No new ideas</span>
          <button
            type="button"
            onClick={fetchSuggestions}
            className="flex bg-transparent border-none text-teal-dark cursor-pointer hover:text-teal"
          >
            <RefreshCw className="size-3" />
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1.5 items-end">
        <div className="inline-flex items-center gap-2 text-[11px] text-teal-dark font-semibold">
          <Sparkles className="size-3" />
          {items.length} idea{items.length !== 1 ? 's' : ''} ·{' '}
          <span className="text-muted-foreground font-medium">{displaySource}</span>
          <button
            type="button"
            onClick={fetchSuggestions}
            title="Refresh"
            className="grid place-items-center w-5 h-5 rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="size-3" />
          </button>
          <button
            type="button"
            onClick={dismissAll}
            title="Hide"
            className="grid place-items-center w-5 h-5 rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="size-3" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end max-w-[480px]">
          {visible.map((it, i) => (
            <span
              key={it + i}
              className="inline-flex items-center bg-card border border-teal/30 rounded-md text-[12px] text-foreground overflow-hidden"
              style={{ animation: 'aiFadeIn .25s ease-out' }}
            >
              <button
                type="button"
                onClick={() => acceptItem(it)}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 border-none bg-transparent cursor-pointer text-foreground text-[12px] font-medium"
              >
                <Plus className="size-2.5 text-teal-dark" />
                {it}
              </button>
              <button
                type="button"
                onClick={() => dismissItem(it)}
                title="Don't suggest again"
                className="grid place-items-center w-5 self-stretch border-none border-l border-l-border bg-transparent text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-2.5" />
              </button>
            </span>
          ))}
          {hiddenCount > 0 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="px-2 py-0.5 border border-dashed border-border rounded-md bg-transparent text-muted-foreground text-[11.5px] cursor-pointer hover:text-foreground"
            >
              + {hiddenCount} more
            </button>
          )}
        </div>
        <style>{`@keyframes aiFadeIn { from { opacity:0; transform:translateY(-2px); } to { opacity:1; transform:none; } }`}</style>
      </div>
    );
  }

  return (
    <div className="w-full mt-2.5 bg-teal-light border border-teal/30 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2 text-[11.5px] text-teal-dark font-semibold">
        <Sparkles className="size-3" />
        Rewrite suggestions · <span className="text-muted-foreground font-medium">{displaySource}</span>
        <button
          type="button"
          onClick={fetchSuggestions}
          title="Refresh"
          className="ml-auto grid place-items-center w-[22px] h-[22px] rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <RefreshCw className="size-3" />
        </button>
        <button
          type="button"
          onClick={dismissAll}
          title="Close"
          className="grid place-items-center w-[22px] h-[22px] rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <X className="size-3" />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="bg-card border border-teal/20 rounded-lg px-3 py-2.5 flex gap-2.5 items-start"
          >
            <div className="flex-1 text-[13px] text-foreground leading-relaxed">{it}</div>
            <div className="flex gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => acceptItem(it)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-[11.5px] font-semibold border text-white cursor-pointer',
                  'bg-coral hover:bg-coral-hover border-coral',
                )}
              >
                Use this
              </button>
              <button
                type="button"
                onClick={() => dismissItem(it)}
                title="Dismiss"
                className="grid place-items-center w-[26px] h-[26px] rounded-md bg-transparent border border-border text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
